import { motion } from "framer-motion";
import { CheckCircle, Circle, UserIcon } from "lucide-react";
import { Button, Progress as ProgressBar, Result, Spin } from "antd";
import apiClient from "QueryClient/axiosClient";
import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "Features/Routes/routeConstants";
import { useUserInfo } from "GlobalStates/User";
import { useMemo } from "react";

export const ProfileCompletion = () => {
  const { user } = useUserInfo();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["user", user?.id],
    queryFn: async () => {
      const data = await apiClient.get(`/users/getProfileMetrics`);
      return data;
    },
    refetchOnWindowFocus: false,
  });

  const navigate = useNavigate();

  const sections = useMemo(() => {
    if (data) {
      const metrics = data?.data?.metrics;
      const sections = [];
      Object.keys(metrics).forEach((key) => {
        sections.push({
          name: key,
          progress: metrics[key] ? 100 : 0,
          completed: metrics[key],
        });
      });
      return sections;
    }
  }, [data]);

  const totalProgress =
    sections?.reduce((acc, section) => acc + section.progress, 0) /
    sections?.length;

  if (isLoading) {
    return (
      <div className="shadow-lg bg-white p-4 rounded-lg flex items-center justify-center h-full w-full">
        <Spin size="large" />
      </div>
    );
  }

  if (isError) {
    return (
      <div className="shadow-lg bg-white p-4 rounded-lg flex items-center justify-center h-full w-full">
        <Result
          status="500"
          title="Something went wrong"
          subTitle="Please try again later"
        />
      </div>
    );
  }

  return (
    <div className="shadow-lg bg-white p-4 rounded-lg h-full">
      <div className="flex items-center justify-between align-center mb-6">
        <h3 className="text-xl font-bold text-gray-900">
          <UserIcon /> Profile Completion
        </h3>
        <div className="text-base text-gray-600">
          {Math.round(totalProgress)}% Complete
        </div>
      </div>

      <div className="space-y-4 mb-6">
        {sections.map((section, index) => (
          <motion.div
            key={section.name}
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
            className="space-y-2"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                {section.completed ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <Circle className="w-5 h-5 text-gray-300" />
                )}
                <span className="font-medium text-base text-gray-900">
                  {section.name}
                </span>
              </div>
            </div>
            <ProgressBar percent={section.progress} strokeColor={"#80e280"} />
          </motion.div>
        ))}
      </div>

      <div className="bg-gray-50 rounded-xl p-4 mb-6">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-gray-700">
            Overall Progress
          </span>
        </div>
        <ProgressBar percent={totalProgress} strokeColor={"#80e280"} />
      </div>

      {totalProgress !== 100 && (
        <Button
          type="primary"
          className="w-full md:w-2/3 h-fit p-2 text-base font-medium"
          onClick={() => {
            navigate(ROUTES.COLLEGE_FINDER);
          }}
        >
          Compelete Profile
        </Button>
      )}
    </div>
  );
};
